import { useState } from "react";
import { Button, Checkbox, Chip, ListItemText, Menu, MenuItem, Stack } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "i18n-fe";

/**
 * Shared "Add filter" dropdown + removable chips (#3240). Replaces rows of
 * on/off switches whose labels wrap. Reused by Class Manager and, next, Class
 * Placings (#3241).
 *
 * Controlled: the parent owns which filters are applied, so the filter logic
 * stays where the list is filtered and this component only renders the state.
 *
 * @param {object} props
 * @param {{ key: string, label: string }[]} props.filters Every filter that
 *   can apply, in the order the dropdown lists them.
 * @param {string[]} props.applied Keys of the filters currently applied.
 * @param {(nextApplied: string[]) => void} props.onChange Called with the new
 *   applied keys when one is added (appended) or removed.
 * @param {string} [props.testIdPrefix] Prefix for data-testids.
 * @param {"add" | "checkbox"} [props.variant] "add" (default, Class Placings):
 *   pick one filter per click, applied ones become chips beside the button.
 *   "checkbox" (#3627, Class Manager): a "Filter (N)" button whose menu lists
 *   every filter as a checkbox and stays open while ticking. It renders no
 *   chips; the page owns its own chip row.
 * @param {Record<string, number>} [props.counts] checkbox variant only: how
 *   many items match each filter (by key), shown beside its label. A `null`
 *   count means "not known yet" and prints an ellipsis, never 0 (#3670).
 */
export default function AddFilterMenu({
  filters,
  applied,
  onChange,
  testIdPrefix = "filter",
  variant = "add",
  counts,
}) {
  const { t } = useTranslation("scoreadmin");
  const [anchorEl, setAnchorEl] = useState(null);

  const appliedSet = new Set(applied);
  const available = filters.filter((f) => !appliedSet.has(f.key));
  const appliedFilters = applied.map((key) => filters.find((f) => f.key === key)).filter(Boolean);

  const closeMenu = () => setAnchorEl(null);

  if (variant === "checkbox") {
    const toggle = (key) =>
      onChange(appliedSet.has(key) ? applied.filter((k) => k !== key) : [...applied, key]);
    return (
      <>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-haspopup="menu"
          data-testid={`${testIdPrefix}-add-button`}
          sx={{ whiteSpace: "nowrap", flexShrink: 0, height: 40 }}
        >
          {appliedFilters.length > 0
            ? t("common.filterWithCount", { count: appliedFilters.length })
            : t("common.filter")}
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {filters.map((f) => (
            <MenuItem
              key={f.key}
              role="menuitemcheckbox"
              aria-checked={appliedSet.has(f.key)}
              data-testid={`${testIdPrefix}-option-${f.key}`}
              onClick={() => toggle(f.key)}
            >
              <Checkbox
                size="small"
                edge="start"
                checked={appliedSet.has(f.key)}
                tabIndex={-1}
                disableRipple
                onChange={() => {}}
                sx={{ pointerEvents: "none" }}
                inputProps={{ "aria-hidden": true }}
              />
              <ListItemText primary={f.label} />
              {counts && (
                <span
                  data-testid={`${testIdPrefix}-count-${f.key}`}
                  style={{ marginLeft: 24, fontVariantNumeric: "tabular-nums", opacity: 0.7 }}
                >
                  {counts[f.key] === null ? "\u2026" : (counts[f.key] ?? 0)}
                </span>
              )}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
      <Button
        size="small"
        variant="outlined"
        startIcon={<FilterListIcon />}
        disabled={available.length === 0}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="menu"
        data-testid={`${testIdPrefix}-add-button`}
        sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
      >
        {t("common.addFilter")}
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl) && available.length > 0} onClose={closeMenu}>
        {available.map((f) => (
          <MenuItem
            key={f.key}
            data-testid={`${testIdPrefix}-option-${f.key}`}
            onClick={() => {
              closeMenu();
              onChange([...applied, f.key]);
            }}
          >
            {f.label}
          </MenuItem>
        ))}
      </Menu>
      {appliedFilters.map((f) => (
        <Chip
          key={f.key}
          size="small"
          color="primary"
          variant="outlined"
          label={f.label}
          data-testid={`${testIdPrefix}-chip-${f.key}`}
          onDelete={() => onChange(applied.filter((k) => k !== f.key))}
          deleteIcon={
            <CloseIcon
              titleAccess={t("common.removeFilter", { filter: f.label })}
              data-testid={`${testIdPrefix}-chip-remove-${f.key}`}
            />
          }
          sx={{ whiteSpace: "nowrap" }}
        />
      ))}
    </Stack>
  );
}
